import { useMemo, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useScrollRestoration } from '@shared-ui/navigation';
import { PageTimingProvider } from '@shared-ui/perf';
import { tenantBranding } from '@shared-ui/api';
import { useIsNonPublicTenant } from '../hooks/useMyTenant';
import { Sidebar } from '@shared-ui/sidebar';
import type { NavItem } from '@shared-ui/sidebar';
import { authApi } from '../api/auth';
import { useAdminUser } from '../hooks/useAdminUser';
import { tenantsApi } from '../api/tenantsApi';
import { threadApi } from '../api/adminThreadApi';
import { ADMIN_FEEDBACK_OWNER_ID } from '@shared-ui/threads';
import { ADMIN_ROLES, visibleNavItemsForRoles, effectiveRoleNames, operatesAcrossTenants } from '../config/adminRoles';
import { platformNavGroup } from '../config/superAdminNav';
import { TenantFilter, useSelectedTenantId, hasScopedTenant } from './TenantFilter';

const SELECT_TENANT_MESSAGE = 'Select a tenant from the dropdown above to open this page.';

/** Collapse dynamic route segments so per-page timing keeps low cardinality —
 *  every visit to a detail page aggregates under one label in Page Perf instead
 *  of fragmenting per id. Mirrors the student app's normalizePageLabel.
 *  `prompts` and `tests` have static siblings (…/new, …/import) that must stay
 *  distinct, hence the negative lookaheads. */
function normalizePageLabel(pathname: string): string {
  return pathname
    .replace(/\/(solution-analysis|submissions|hints|concept-lab|learning-moments|questions|users|books|evals|datasets|operations|question-sets|analysis)\/[0-9a-f-]{8,}[^/]*/i, '/$1/:id')
    .replace(/\/prompts\/(?!new)[^/]+/, '/prompts/:id')
    .replace(/\/tests\/(?!new|import)[^/]+/, '/tests/:id');
}

export function Layout() {
  const mainRef = useRef<HTMLElement>(null);
  useScrollRestoration(mainRef);
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAdminUser();
  const isSuperAdmin = user?.isSuperAdmin === true;
  // Who gets the tenant filter: anyone whose pages show every tenant's people.
  const crossTenant = operatesAcrossTenants(user);
  const selectedTenantId = useSelectedTenantId();
  const tenantUnselected = isSuperAdmin && !hasScopedTenant(selectedTenantId);

  // User Feedback badge = items still needing the team: status open or
  // in_progress. Not an unread count — admins never mark feedback seen, so that
  // counted every item this admin hadn't personally replied to (closed ones too).
  // Same list endpoint and tenant scope as the User Feedback page; limit 1 because
  // only `total` is used.
  const { data: openFeedback } = useQuery({
    queryKey: ['admin-feedback-open'],
    queryFn: () => threadApi.list({ type: 'app_feedback', ownerId: ADMIN_FEEDBACK_OWNER_ID, status: 'open,in_progress', limit: 1 }),
    refetchInterval: 60000,
    enabled: !isSuperAdmin,
  });
  const feedbackBadge = openFeedback?.total || 0;

  // Student–teacher messages carry the same unread badge as User Feedback. No ownerId
  // filter — an admin watches every teacher's queue, not one owner's.
  const { data: doubtsUnread } = useQuery({
    queryKey: ['admin-doubts-unread'],
    queryFn: () => threadApi.getUnreadCount({ type: 'student_teacher' }),
    refetchInterval: 60000,
    enabled: !isSuperAdmin,
  });
  const doubtsBadge = doubtsUnread?.count || 0;

  // Tenant branding (logo) for the sidebar — shown for a tenant admin's own
  // tenant. Super-admins resolve to the platform stub (no logo). Absent → default.
  const { data: myTenant } = useQuery({
    queryKey: ['my-tenant'],
    queryFn: () => tenantsApi.getMine(),
    staleTime: 5 * 60 * 1000,
  });

  // Every Dashboard tab answers a question about the PLATFORM, not about one
  // school — so the entry goes for a tenant admin rather than being shown with
  // most of its tabs withheld. `IndexRedirect` lands them elsewhere.
  const isNonPublicTenant = useIsNonPublicTenant();

  const navItems: NavItem[] = useMemo(() => {
    // Super-admins see the same admin pages as a tenant admin would, prefixed
    // by a Platform group with cross-tenant surfaces. The tenant filter in
    // topSlot scopes which tenant's data the shared pages display.
    // Nav is driven by the roles the admin actually holds, derived from their
    // scoped assignments (falling back to legacy roles[] pre-backfill).
    const roles: readonly string[] = isSuperAdmin
      ? [...ADMIN_ROLES]
      : effectiveRoleNames(user);

    const adminGroups = visibleNavItemsForRoles(roles, leaf => {
      const overrides: Partial<NavItem> = {};
      if (leaf.path === '/user-feedback' && feedbackBadge) overrides.badge = feedbackBadge;
      if (leaf.path === '/help-queries' && doubtsBadge) overrides.badge = doubtsBadge;
      if (tenantUnselected && leaf.requiresTenantForSuperAdmin) {
        overrides.disabled = true;
        overrides.disabledMessage = SELECT_TENANT_MESSAGE;
      }
      return overrides;
    });

    const groups = isNonPublicTenant
      ? adminGroups.filter(item => item.path !== '/')
      : adminGroups;
    const withPlatform = isSuperAdmin ? [platformNavGroup, ...groups] : groups;
    // Appended after the role-driven groups rather than declared inside them:
    // every admin has an account whatever they administer, so it belongs to no
    // role and must not disappear when one is withheld. `bottom` pins it to the
    // rail's foot, where the other two apps put theirs.
    return [...withPlatform, { path: '/you', label: 'You', icon: '👤', bottom: true }];
  }, [isSuperAdmin, user?.roles, user?.roleAssignments, feedbackBadge, doubtsBadge, tenantUnselected, isNonPublicTenant]);

  return (
    <div className="sidebar-layout admin-shell">
      <Sidebar
        subtitle="Admin Console"
        userName={user?.name?.split(' ')[0]}
        navItems={navItems}
        topSlot={crossTenant ? <TenantFilter /> : undefined}
        onLogout={() => { authApi.logout(); navigate('/login'); }}
        {...tenantBranding(myTenant)}
      />
      <main ref={mainRef} className="sidebar-layout-content">
        {/* One page-load record per navigation (mount → fetch idle) to
            /console/client-logs. Viewer: Monitoring → Page Perf. */}
        <PageTimingProvider navKey={location.key} page={normalizePageLabel(location.pathname)}>
          <Outlet />
        </PageTimingProvider>
      </main>
    </div>
  );
}
